/**
 * Stellar module for transaction building with Blockdaemon Horizon endpoints.
 *
 * @example
 * ```typescript
 * import { BdSdk } from "@blockdaemon/bd-sdk";
 *
 * const sdk = new BdSdk({ bdApiKey: "your-api-key" });
 *
 * const chainId = "stellar:pubnet";
 * const server = sdk.stellar.server(chainId);
 * const account = await server.loadAccount(publicKey);
 *
 * const transaction = new sdk.stellar.TransactionBuilder(account, {
 *   fee: sdk.stellar.BASE_FEE,
 *   networkPassphrase: sdk.stellar.getNetworkPassphrase(chainId),
 * })
 *   .addOperation(
 *     sdk.stellar.Operation.payment({
 *       destination: recipient,
 *       asset: sdk.stellar.Asset.native(),
 *       amount: "10",
 *     })
 *   )
 *   .setTimeout(180)
 *   .build();
 *
 * const xdr = transaction.toXDR();
 * ```
 *
 * @packageDocumentation
 */
import { Account, Asset, BASE_FEE, FeeBumpTransaction, Horizon, Keypair, Memo, Networks, Operation, StrKey, TimeoutInfinite, Transaction, TransactionBuilder, xdr, } from "@stellar/stellar-sdk";
import { DEFAULT_RPC_BASE_PATH } from "../../shared/constants";
function getHorizonUrl(chainId, rpcBasePath) {
    const networkPath = chainId === "stellar:pubnet" ? "mainnet" : "testnet";
    return `${rpcBasePath}/stellar/${networkPath}/native`;
}
function getNetworkPassphraseForChain(chainId) {
    return chainId === "stellar:pubnet" ? Networks.PUBLIC : Networks.TESTNET;
}
/**
 * Create a Stellar module configured with Blockdaemon endpoints.
 * @internal
 */
export function createStellarModule(config) {
    const { bdApiKey, rpcBasePath = DEFAULT_RPC_BASE_PATH } = config;
    const serverCache = new Map();
    const getServer = (chainId) => {
        let serverInstance = serverCache.get(chainId);
        if (!serverInstance) {
            const horizonUrl = getHorizonUrl(chainId, rpcBasePath);
            serverInstance = new Horizon.Server(horizonUrl, {
                headers: {
                    "X-API-Key": bdApiKey,
                },
            });
            serverCache.set(chainId, serverInstance);
        }
        return serverInstance;
    };
    return {
        Asset,
        Memo,
        Operation,
        TransactionBuilder,
        Transaction,
        FeeBumpTransaction,
        Account,
        Keypair,
        Networks,
        BASE_FEE,
        TimeoutInfinite,
        StrKey,
        xdr,
        server: getServer,
        getNetworkPassphrase: getNetworkPassphraseForChain,
        Horizon: {
            Server: Horizon.Server,
        },
    };
}
//# sourceMappingURL=data:application/json;base64,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